import { useMemo } from 'react';
import {
    Box,
    Card,
    Form,
    Icon,
    IconButton,
    Typography,
    useAppDispatch,
    useAppSelector,
    useTheme,
} from '../../../base';
import { useTranslation } from 'react-i18next';
import { jobsActions, jobsSelectors } from '../../../actions';
import { RoutePaths } from '../../../routes/RoutePaths';
import { useLocation, useNavigate } from 'react-router-dom';
import { Control, useForm } from 'react-hook-form';
import {
    ApplyNowFormValues,
    emptyApplyNowAdditionalQuestionsForm,
    IApplyNowAdditionalQuestionsForm,
    IJob,
    JobStatus,
    QuestionAnswerFields,
    QuestionMode,
} from '../../../common';
import { QuestionsList } from '../../SaveJob/SaveJobSteps/JobQuestions/components';
import { StyledQuestionsListContainer } from '../../SaveJob/SaveJobSteps/JobQuestions/JobQuestions.styles';

interface Props {
    isEmployerView?: boolean;
    isPreview?: boolean;
}

const JobQuestionsViewer = ({
    isEmployerView = false,
    isPreview = false,
}: Props) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const stepperInfo = useAppSelector(jobsSelectors.getJobStepperInfo);
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const location = useLocation();

    const activeJob: IJob =
        useAppSelector(jobsSelectors.getActiveJob) || ({} as IJob);
    const jobQuestions = useMemo(
        () => activeJob.applyQuestions || [],
        [activeJob.applyQuestions]
    );

    const defaultValues = useMemo(
        (): IApplyNowAdditionalQuestionsForm => ({
            ...emptyApplyNowAdditionalQuestionsForm,
            [ApplyNowFormValues.questionAnswers]: jobQuestions.map((q) => ({
                [QuestionAnswerFields.QUESTION_ID]: q.id,
                [QuestionAnswerFields.ANSWER]: '',
            })),
        }),
        [jobQuestions]
    );

    const methods = useForm<IApplyNowAdditionalQuestionsForm>({
        defaultValues,
        values: defaultValues,
    });

    const onEditClick = () => {
        dispatch(
            jobsActions.setStepperInfo({
                stepperInfo: {
                    ...stepperInfo,
                    activeStepIndex: 3,
                },
            })
        );
        if (!location.pathname.includes(RoutePaths.saveJob)) {
            navigate(`${location.pathname}/save-job${location.search}`);
        }
    };

    return (
        <Card
            sx={{
                paddingTop: theme.spacing(3),
                boxShadow: 'none',
                border: isPreview
                    ? `1px solid ${theme.palette.grey[400]}`
                    : 'none',
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 2,
                }}
            >
                <Typography variant="h4" color="black">
                    {t('positionPage.questions')}
                </Typography>

                <Box display="flex" justifyContent="flex-end">
                    {(isEmployerView || isPreview) &&
                        activeJob.status !== JobStatus.Legacy && (
                            <IconButton
                                onClick={onEditClick}
                                sx={{ marginInlineEnd: theme.spacing(-2) }}
                            >
                                <Icon name={'EditIcon'} />
                            </IconButton>
                        )}
                </Box>
            </Box>
            <StyledQuestionsListContainer isConfiguration={false}>
                {jobQuestions.length === 0 ? (
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ py: 2 }}
                    >
                        {t('saveJob.noQuestionsYet')}
                    </Typography>
                ) : (
                    <Form methods={methods}>
                        <QuestionsList
                            control={
                                methods.control as Control<IApplyNowAdditionalQuestionsForm>
                            }
                            mode={QuestionMode.VIEW}
                            applyQuestions={jobQuestions}
                        />
                    </Form>
                )}
            </StyledQuestionsListContainer>
        </Card>
    );
};

export default JobQuestionsViewer;
